<script setup lang="ts">
import { type PropType, ref, watchEffect } from 'vue'
import { useMandatoryFieldError } from '../../model/useMandatoryFieldError'

const props = defineProps({
  type: {
    type: String as PropType<'number' | 'percent'>,
    default: 'number',
  },
  max: {
    type: Number,
    default: 1000000000,
  },
  min: {
    type: Number,
    default: -1000000000,
  },
  precision: {
    type: Number,
    default: 100,
  },
  use_min: {
    type: Boolean,
    default: false,
  },
  use_max: {
    type: Boolean,
    default: false,
  },
  hideDetails: {
    type: Boolean,
    default: true,
  },
  label: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  mandatory: {
    default: false,
  },
  showMandatoryError: {
    type: Boolean,
    default: false,
  },
})

const value = defineModel<string | number | null>({ default: null })
const currentValue = ref<number | string>('')
const {
  visible: mandatoryErrorVisible,
  message: mandatoryErrorMessage,
  markTouched,
  registerUserInteraction,
} = useMandatoryFieldError(
  () => ({
    showMandatoryError: props.showMandatoryError,
    mandatory: props.mandatory,
    type: props.type,
  }),
  () => value.value,
)

/** Ограничивает число по min/max только когда граница явно включена флагом. */
function clampNumber(num: number): number {
  let result = num
  if (props.use_max) {
    const max = Number(props.max)
    if (Number.isFinite(max) && result > max) result = max
  }
  if (props.use_min) {
    const min = Number(props.min)
    if (Number.isFinite(min) && result < min) result = min
  }
  return result
}

function toDisplayNumber(raw: string | number | null | undefined): number | string {
  if (raw === null || raw === undefined || raw === '') return ''
  const num = Number(raw)
  if (!Number.isFinite(num)) return ''
  return props.type === 'percent' ? num * 100 : num
}

/** Устанавливаем значение из model-value */
watchEffect(() => {
  currentValue.value = toDisplayNumber(value.value)
})

/**
 * Функция обработки потери фокуса
 */
const blurHandler = () => {
  markTouched()
  // Пустой ввод не превращаем в 0 — иначе обязательное число «заполнено».
  if (currentValue.value === '' || currentValue.value === null || currentValue.value === undefined) {
    value.value = null
  }
}
const inputHandler = () => {
  if (currentValue.value === '' || currentValue.value === null || currentValue.value === undefined) {
    value.value = null
    return
  }
  const numberValue = clampNumber(Number(currentValue.value))
  if (!Number.isFinite(numberValue)) {
    value.value = null
    currentValue.value = ''
    return
  }
  currentValue.value = numberValue

  if (props.type === 'percent') {
    value.value = numberValue / 100
  } else {
    value.value = numberValue
  }
}

/**
 * Функция обработки нажатия клавиши
 */
const keyDownHandler = () => {
  currentValue.value = Number(Number(currentValue.value).toFixed(props.precision))
}
</script>

<template>
  <VTextField
    type="number"
    autocomplete="off"
    :label="label"
    :disabled="disabled"
    :loading="loading"
    :hide-details="mandatoryErrorVisible ? false : hideDetails"
    :error="mandatoryErrorVisible ? true : undefined"
    :error-messages="mandatoryErrorVisible ? mandatoryErrorMessage : undefined"
    v-model="currentValue"
    @pointerdown="registerUserInteraction()"
    @blur="blurHandler"
    @input="inputHandler"
    @keydown="keyDownHandler"
  />
</template>
